import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X, Send, CheckCircle2, Loader2 } from 'lucide-react';

const MR = { fontFamily: 'Manrope, sans-serif' };
const JB = { fontFamily: "'JetBrains Mono', monospace" };
const API = process.env.REACT_APP_BACKEND_URL;

export function PilotModal({ open, onClose, clubName, dt }) {
  const m = dt.modal;
  const [form, setForm] = useState({ name: '', email: '', role: '', message: '' });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const overlayRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm({ name: '', email: '', role: '', message: m.defaultMessage(clubName) });
      setConsent(false);
      setDone(false);
      setError('');
    }
  }, [open, clubName, m]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/pilot-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ club_name: clubName, ...form }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Submission failed');
      }
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }, [clubName, form]);

  if (!open) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      data-testid="pilot-modal-overlay"
    >
      <div
        className="relative w-full max-w-md rounded-2xl border border-border/50 bg-card shadow-2xl animate-in zoom-in-95 duration-200"
        data-testid="pilot-modal"
      >
        <button onClick={onClose} className="absolute top-4 right-4 text-muted-foreground/60 hover:text-foreground transition-colors" data-testid="pilot-modal-close">
          <X className="w-5 h-5" />
        </button>

        <div className="p-7">
          {done ? (
            <div className="text-center py-6 space-y-4" data-testid="pilot-modal-success">
              <div className="w-14 h-14 rounded-full bg-[hsl(150,50%,45%)]/10 border border-[hsl(150,50%,45%)]/20 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-7 h-7 text-[hsl(150,50%,45%)]" />
              </div>
              <h3 className="text-xl font-bold text-foreground" style={MR}>{m.successTitle}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto">
                {m.successText(clubName)}
              </p>
              <Button variant="outline" size="sm" onClick={onClose} className="mt-2 rounded-lg">{m.close}</Button>
            </div>
          ) : (
            <>
              <h3 className="text-lg font-bold text-foreground mb-1 pr-8" style={MR} data-testid="pilot-modal-title">
                {m.title}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                {m.subtitle}
              </p>

              <div className="flex items-center gap-2 mb-6 px-3 py-2 rounded-lg border border-border/40 bg-background/50 w-fit" data-testid="pilot-modal-club">
                <span className="text-[11px] text-muted-foreground/50 uppercase tracking-wider" style={JB}>{m.clubLabel}</span>
                <span className="text-sm font-medium text-foreground">{clubName}</span>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="pilot-form">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground/70 uppercase tracking-wider" style={MR}>{m.nameLabel}</label>
                  <Input value={form.name} onChange={set('name')} required placeholder={m.namePlaceholder} className="bg-background border-border/50 rounded-lg h-10" data-testid="pilot-name" />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground/70 uppercase tracking-wider" style={MR}>{m.emailLabel}</label>
                  <Input type="email" value={form.email} onChange={set('email')} required placeholder={m.emailPlaceholder} className="bg-background border-border/50 rounded-lg h-10" data-testid="pilot-email" />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground/70 uppercase tracking-wider" style={MR}>{m.roleLabel} <span className="text-muted-foreground/40">{m.roleOptional}</span></label>
                  <Input value={form.role} onChange={set('role')} placeholder={m.rolePlaceholder} className="bg-background border-border/50 rounded-lg h-10" data-testid="pilot-role" />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground/70 uppercase tracking-wider" style={MR}>{m.messageLabel} <span className="text-muted-foreground/40">{m.messageOptional}</span></label>
                  <textarea
                    value={form.message}
                    onChange={set('message')}
                    rows={2}
                    className="w-full rounded-lg border border-border/50 bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                    data-testid="pilot-message"
                  />
                </div>

                {error && <p className="text-sm text-destructive" data-testid="pilot-error">{error}</p>}

                <label className="flex items-start gap-2.5 cursor-pointer" data-testid="pilot-consent-label">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    required
                    className="mt-0.5 h-4 w-4 rounded border-border/50 accent-primary flex-shrink-0"
                    data-testid="pilot-consent"
                  />
                  <span className="text-xs text-muted-foreground leading-relaxed">
                    {m.consent}{' '}
                    <Link to="/privacy-policy" target="_blank" className="text-primary hover:underline">{m.consentLink}</Link>
                    {' '}{m.consentEnd}
                  </span>
                </label>

                <Button type="submit" className="w-full bg-primary hover:bg-primary/90 h-11 rounded-lg font-medium" disabled={submitting || !consent} data-testid="pilot-submit">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> {m.submit}</>}
                </Button>

                <p className="text-[11px] text-center text-muted-foreground/40">
                  {m.disclaimer}
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
